import { EnumRichiestaDTOStato, RichiestaDTO } from '@framework/api-client';
import { TaskName } from '@framework/common-web';
import {
  SubmissionLogic,
  SubmissionStateTag,
} from '@framework/frontoffice-submission';
import { t } from '@framework/i18n';
import { ISODate, TableCellEllipsis } from '@framework/ui';
import { theme } from '#theme';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { ColumnGroupType } from 'antd/lib/table';
import { ApplicantsTableCell } from './ApplicantsTableCell';

const dateFormat = 'DD/MM/YYYY HH:mm';

export const REQUEST_STATUSES = [
  {
    label: t('submission-columns.creata', 'Creata'),
    value: EnumRichiestaDTOStato.CREATA,
  },
  {
    label: t('submission-columns.bozza', 'Bozza'),
    value: EnumRichiestaDTOStato.BOZZA,
  },
  {
    label: t('submission-columns.integrata', 'Integrata'),
    value: EnumRichiestaDTOStato.INTEGRATA,
  },
  {
    label: t('submission-columns.inviata', 'Inviata'),
    value: EnumRichiestaDTOStato.INVIATA,
  },
  {
    label: t('submission-columns.ricevuta', 'Ricevuta'),
    value: EnumRichiestaDTOStato.RICEVUTA,
  },
  {
    label: t('submission-columns.conclusa', 'Conclusa'),
    value: EnumRichiestaDTOStato.CONCLUSA,
  },
];

export const submissionsColumnsBase = {
  idTipoDomanda: {
    title: t('submission-columns.domanda', 'Domanda'),
    dataIndex: 'idTipoDomanda',
    key: 'idTipoDomanda',
    sorter: true,
    sortDataIndex: 'idTipoDomanda',
    render: (_, record) => record.idTipoDomanda,
  },
  titoloTipoDomanda: {
    title: t('submission-columns.domanda', 'Domanda'),
    dataIndex: 'titoloTipoDomanda',
    key: 'titoloTipoDomanda',
    sorter: true,
    sortDataIndex: 'titoloTipoDomanda',
    render: (_, record) => (
      <TableCellEllipsis
        tooltip={false}
        width={170}
        children={record.titoloTipoDomanda}
      />
    ),
  },
  stato: {
    title: t('submission-columns.stato', 'Stato'),
    dataIndex: 'statoRichiesta',
    key: 'statoRichiesta',
    sorter: true,
    sortDataIndex: 'statoRichiesta',
    render: (_, record) => <SubmissionStateTag submission={record} />,
  },
  fase: {
    title: t('submission-columns.fase', 'Fase'),
    dataIndex: 'fase.denominazione',
    key: 'fase.denominazione',
    width: 120,
    sorter: true,
    sortDataIndex: 'fase.denominazione',
    render: (_, record) => <TaskName>{record.fase?.denominazione}</TaskName>,
  },
  dataCreazione: {
    title: t('submission-columns.data-creazione', 'Data creazione'),
    dataIndex: 'dataCreazione',
    key: 'dataCreazione',
    width: 100,
    sorter: true,
    sortDataIndex: 'dataCreazione',
    render: date => <ISODate date={date} format={dateFormat} />,
  },
  dataModifica: {
    title: t('submission-columns.data-modifica', 'Data modifica'),
    dataIndex: 'dataModifica',
    key: 'dataModifica',
    width: 100,
    sorter: true,
    sortDataIndex: 'dataModifica',
    render: date => <ISODate date={date} format={dateFormat} />,
  },
  dataInvio: {
    title: t('submission-columns.data-invio', 'Data invio'),
    dataIndex: 'dataInvio',
    key: 'dataInvio',
    width: 100,
    sorter: true,
    sortDataIndex: 'dataInvio',
    render: date => <ISODate date={date} format={dateFormat} />,
  },
  oggetto: {
    title: t('submission-columns.oggetto', 'Oggetto'),
    dataIndex: 'oggetto',
    key: 'oggetto',
    sortDataIndex: 'oggetto',
    render: (_, record) => (
      <TableCellEllipsis
        children={record.oggetto}
        tooltip={false}
        width={170}
        // expandable={false}
      />
    ),
  },
  // progressivo: {
  //   title: t('submission-columns.numero-istanza', 'Numero Istanza'),
  //   dataIndex: 'progressivo',
  //   key: 'progressivo',
  //   sorter: true,
  //   sortDataIndex: 'progressivo',
  //   render: (_, record) => record.progressivo,
  // },
  protocollo: {
    title: t('submission-columns.numero-protocollo', 'Numero Protocollo'),
    dataIndex: 'numeroProtocollo',
    key: 'numeroProtocollo',
    width: 120,
    sorter: true,
    sortDataIndex: 'numeroProtocollo',
    render: (_, record) => record.numeroProtocollo,
  },
  datiTitolari: {
    title: t('submission-columns.titolare', 'Titolare'),
    dataIndex: 'datiTitolari',
    key: 'datiTitolari',
    width: 120,
    render: (_, record) => {
      const applicants = SubmissionLogic.getApplicant(record);

      if (!applicants) return null;
      return <ApplicantsTableCell applicants={applicants} />;

      // return (
      //   <Space>
      //     {applicants.mainOwner}
      //     {applicants.multipleApplicants && (
      //       <Popover
      //         title="Altri titolari"
      //         content={
      //           <ul>
      //             {applicants.others.map((name, index) => (
      //               <li key={index}>{name}</li>
      //             ))}
      //           </ul>
      //         }
      //       >
      //         <TeamOutlined />
      //       </Popover>
      //     )}
      //   </Space>
      // );
    },
  },
} satisfies Record<
  string,
  ColumnGroupType<RichiestaDTO> | ColumnType<RichiestaDTO>
>;

/**
 * Configuriamo quale colonna deve essere visualizzata come numero
 * identificativo
 */
const subsmissionNumberColumn = theme.showProtocolNumberAsSubmissionNumberColumn
  ? submissionsColumnsBase.protocollo
  : {};
// : submissionsColumnsBase.progressivo;

export const submissionsColumns: ColumnsType<RichiestaDTO> = [
  submissionsColumnsBase.stato,
  ...(theme.showPhaseSubmissionColumn ? [submissionsColumnsBase.fase] : []),
  subsmissionNumberColumn,
  submissionsColumnsBase.titoloTipoDomanda,
  submissionsColumnsBase.oggetto,
  submissionsColumnsBase.datiTitolari,
  submissionsColumnsBase.dataInvio,
  submissionsColumnsBase.dataCreazione,
  submissionsColumnsBase.dataModifica,
];

export const submissionsColumnsMobile: ColumnsType<RichiestaDTO> = [
  submissionsColumnsBase.stato,
  submissionsColumnsBase.titoloTipoDomanda,
  submissionsColumnsBase.dataInvio,
];
